// @spec SHL-STR-016
// @spec SHL-STR-017
// @spec SHL-STR-018
// @spec SHL-STR-019
// @spec SHL-STR-020
'use client';

import { useLocation } from '@tanstack/react-router';
import { Command } from 'cmdk';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { RecentSearches } from './RecentSearches';
import { SearchFilters } from './SearchFilters';
import { SearchInput } from './SearchInput';
import { SearchResults, type SearchResultSelection } from './SearchResults';

import { CommandPalette } from '@/components/command-palette';
import { useCommandPalette } from '@/hooks/use-command-palette';
import { useRecentSearches } from '@/hooks/use-recent-searches';
import { useSearch } from '@/hooks/use-search';
import { useSearchNavigation } from '@/hooks/use-search-navigation';
import { trackSearchResultClick } from '@/lib/search-analytics';
import { cn } from '@/lib/utils';

export interface GlobalSearchProps {
  /** Controlled open state */
  open: boolean;
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Optional additional CSS classes for the dialog */
  className?: string;
}

/**
 * Global search overlay/dialog component.
 *
 * Renders as a centered dialog using cmdk's Command.Dialog with:
 * - Auto-focused input when opened
 * - Recent searches when input is empty
 * - Search results grouped by category when searching
 * - Command palette mode when input starts with / or >
 * - Escape key and backdrop click to close
 * - Smooth fade-in/zoom-in animation
 *
 * This component provides the foundation for:
 * - SHL-STR-017 (Search Across Platform) - Implemented
 * - SHL-STR-018 (Filter Search Results) - Implemented
 * - SHL-STR-020 (Command Palette) - Implemented
 *
 * @example
 * const { isOpen, closeSearch } = useGlobalSearch();
 *
 * <GlobalSearch
 *   open={isOpen}
 *   onOpenChange={(open) => !open && closeSearch()}
 * />
 */
export function GlobalSearch({ open, onOpenChange, className }: GlobalSearchProps) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const { recentSearches, addSearch, removeSearch, clearAll } = useRecentSearches();
  const {
    query,
    setQuery,
    results,
    isLoading,
    hasSearched,
    executeSearch,
    // Filter state
    selectedCategories,
    toggleCategory,
    dateRange,
    setDateRange,
    clearFilters,
    hasActiveFilters,
    availableCategories,
  } = useSearch();
  const { executeCommand, isCommandMode, getCommandFilter } = useCommandPalette();
  const { navigateToResult, restoreSearchState, clearSearchState, hasSearchStateParam } =
    useSearchNavigation(query);

  // Determine if we're in command mode
  const commandMode = isCommandMode(query);
  const commandFilter = commandMode ? getCommandFilter(query) : '';

  // Restore search state when returning from a result page
  React.useEffect(() => {
    if (hasSearchStateParam && !open) {
      const savedState = restoreSearchState();
      if (savedState) {
        setQuery(savedState.query);
        onOpenChange(true);
        clearSearchState();
      }
    }
  }, [hasSearchStateParam, open, restoreSearchState, setQuery, onOpenChange, clearSearchState]);

  // Reset search and filters when dialog closes
  React.useEffect(() => {
    if (!open) {
      setQuery('');
      clearFilters();
    }
  }, [open, setQuery, clearFilters]);

  const handleSelectRecentSearch = React.useCallback(
    (term: string) => {
      setQuery(term);
      // Move to top of recent searches
      addSearch(term);
    },
    [setQuery, addSearch]
  );

  const handleResultSelect = React.useCallback(
    (selection: SearchResultSelection) => {
      const { result, category, position } = selection;

      // Add to recent searches
      executeSearch();

      // Track analytics event
      trackSearchResultClick({
        query,
        resultId: result.id,
        resultType: category,
        resultUrl: result.url,
        position,
        newTab: false,
      });

      // Navigate to result URL with state preservation
      navigateToResult(result.url, { highlightTerm: query });

      // Close the dialog
      onOpenChange(false);
    },
    [executeSearch, query, navigateToResult, onOpenChange]
  );

  const handleResultSelectNewTab = React.useCallback(
    (selection: SearchResultSelection) => {
      const { result, category, position } = selection;

      // Add to recent searches
      executeSearch();

      // Track analytics event
      trackSearchResultClick({
        query,
        resultId: result.id,
        resultType: category,
        resultUrl: result.url,
        position,
        newTab: true,
      });

      // Open in new tab with state preservation
      navigateToResult(result.url, { newTab: true, highlightTerm: query });

      // Close the dialog
      onOpenChange(false);
    },
    [executeSearch, query, navigateToResult, onOpenChange]
  );

  const handleCommandSelect = React.useCallback(
    (commandId: string) => {
      void executeCommand(commandId).then(() => {
        onOpenChange(false);
      });
    },
    [executeCommand, onOpenChange]
  );

  // Get placeholder text based on mode
  const placeholder = commandMode ? t('search.typeCommand') : t('search.placeholder');

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label={t('search.globalCommandMenu')}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 w-full max-w-[640px] translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded-xl border border-border bg-background shadow-2xl',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        className
      )}
      overlayClassName="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      contentClassName=""
    >
      <SearchInput value={query} onValueChange={setQuery} placeholder={placeholder} />

      {/* Show filters when searching (not in command mode) */}
      {!commandMode && hasSearched && (
        <SearchFilters
          availableCategories={availableCategories}
          selectedCategories={selectedCategories}
          onToggleCategory={toggleCategory}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          onClearAll={clearFilters}
          hasActiveFilters={hasActiveFilters}
        />
      )}

      <Command.List className="max-h-[400px] overflow-y-auto p-2">
        {/* Command Palette Mode */}
        {commandMode && (
          <CommandPalette
            filter={commandFilter}
            onSelect={handleCommandSelect}
            currentPath={location.pathname}
          />
        )}

        {/* Search Mode - Show recent searches when there's no search input */}
        {!commandMode && !query && (
          <RecentSearches
            searches={recentSearches}
            onSelect={handleSelectRecentSearch}
            onRemove={removeSearch}
            onClearAll={clearAll}
          />
        )}

        {/* Show search results when searching (not in command mode) */}
        {!commandMode && hasSearched && (
          <SearchResults
            results={results}
            isLoading={isLoading}
            query={query}
            onSelect={handleResultSelect}
            onSelectNewTab={handleResultSelectNewTab}
          />
        )}

        {/* Show hint when query is too short (not in command mode) */}
        {!commandMode && query && !hasSearched && (
          <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
            {t('search.typeToSearch')}
          </Command.Empty>
        )}
      </Command.List>

      {/* Mode indicator */}
      <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
        {commandMode ? (
          <span>
            <kbd className="mr-1 rounded border border-border bg-muted px-1 font-mono">↑↓</kbd>
            {t('search.navigate')}
            <kbd className="mx-1 ml-3 rounded border border-border bg-muted px-1 font-mono">↵</kbd>
            {t('search.select')}
          </span>
        ) : hasSearched ? (
          <span>
            <kbd className="mr-1 rounded border border-border bg-muted px-1 font-mono">↑↓</kbd>
            {t('search.navigate')}
            <kbd className="mx-1 ml-3 rounded border border-border bg-muted px-1 font-mono">↵</kbd>
            {t('search.select')}
            <kbd className="mx-1 ml-3 rounded border border-border bg-muted px-1 font-mono">⌘↵</kbd>
            {t('search.newTab')}
          </span>
        ) : (
          <span>
            {t('search.commandHint')}
          </span>
        )}
      </div>
    </Command.Dialog>
  );
}
